// Admin — Driver detail (Wave E).

import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Pencil, Power } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Badge } from '../../../components/ui/Badge/Badge';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { useDriverDetail, useSetDriverStatus } from './hooks/useDriversAdmin';
import styles from './DriverFormPage.module.css';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function DriverDetailPage(): JSX.Element {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const detail = useDriverDetail(id);
  const setStatus = useSetDriverStatus();

  if (detail.isLoading) {
    return (
      <div className={styles.page}>
        <PageHeader title="Driver" />
        <Card>
          <div className={styles.empty}>
            <Spinner />
          </div>
        </Card>
      </div>
    );
  }

  if (!detail.data) {
    return (
      <div className={styles.page}>
        <PageHeader title="Driver" />
        <Card>
          <div className={styles.empty}>Driver not found.</div>
        </Card>
      </div>
    );
  }

  const driver = detail.data;
  const nextStatus: 'active' | 'deactivated' =
    driver.status === 'active' ? 'deactivated' : 'active';

  return (
    <div className={styles.page}>
      <PageHeader
        title={driver.name}
        subtitle={`@${driver.username} · Driver`}
        actions={
          <div className={styles.actions}>
            <Button
              variant="ghost"
              icon={ArrowLeft}
              onClick={() => navigate('/admin/drivers')}
            >
              Back
            </Button>
            <Button
              variant="secondary"
              icon={Power}
              onClick={() =>
                setStatus.mutate({ uuid: driver.uuid, status: nextStatus })
              }
              loading={setStatus.isPending}
            >
              {driver.status === 'active' ? 'Deactivate' : 'Reactivate'}
            </Button>
            <Button
              variant="primary"
              icon={Pencil}
              onClick={() =>
                navigate(`/admin/drivers/${encodeURIComponent(driver.uuid)}/edit`)
              }
            >
              Edit
            </Button>
          </div>
        }
      />

      <Card title="Profile">
        <div className={styles.detailGrid}>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Name</span>
            <span className={styles.detailValue}>{driver.name}</span>
          </div>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Username</span>
            <span className={styles.detailValue}>{driver.username}</span>
          </div>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Phone</span>
            <span className={styles.detailValue}>{driver.phone}</span>
          </div>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Email</span>
            <span className={styles.detailValue}>{driver.email ?? '—'}</span>
          </div>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Postcode</span>
            <span className={styles.detailValue}>{driver.postcode ?? '—'}</span>
          </div>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Vehicle registration</span>
            <span className={styles.detailValue}>{driver.vehicle_registration ?? '—'}</span>
          </div>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Address</span>
            <span className={styles.detailValue}>{driver.address ?? '—'}</span>
          </div>
        </div>
      </Card>

      <Card title="Account">
        <div className={styles.detailGrid}>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Status</span>
            <span className={styles.detailValue}>
              {driver.status === 'active' ? (
                <Badge variant="success">Active</Badge>
              ) : (
                <Badge variant="muted">Deactivated</Badge>
              )}
            </span>
          </div>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Last Login</span>
            <span className={styles.detailValue}>
              {formatDate(driver.last_login_at)}
            </span>
          </div>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Created</span>
            <span className={styles.detailValue}>
              {formatDate(driver.created_at)}
            </span>
          </div>
        </div>
      </Card>
    </div>
  );
}
